<template>
  <div class="h-full">
    <Line class="h-full" :data="data" :options="options"></Line>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

const specialTickPlugin = {
  id: 'specialTickPlugin',
  afterDraw(chart) {
    const xAxis = chart.scales.x;
    const ctx = chart.ctx;
    const ticks = xAxis.ticks;
    ctx.save();
    ticks.forEach((tick, i) => {
      const label = tick.label;
      if (!label) return;
      const day = Array.isArray(label) ? label[0] : label.split('/')[0];
      if (day === '01') {
        const x = xAxis.getPixelForTick(i);
        const yTop = chart.chartArea.top;      // Haut du graphique
        const yBottom = xAxis.top;          // Axe X
        ctx.strokeStyle = '#E8535D';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, yTop);
        ctx.lineTo(x, yBottom);
        ctx.stroke();
      }
    });
    ctx.restore();
  }
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  specialTickPlugin
)

export default {
  name: 'PaymentChart',
  components: {
    Line
  },
  props: ['payments', 'startDate', 'endDate'],
  data() {
    return {
      data: {
        labels: [],
        datasets: [
          {
            label: 'Trésorerie',
            backgroundColor: '#f87979',
            data: [],
            pointBackgroundColor: ctx => ctx.raw < 0 ? 'red' : '#f87979',
            pointRadius: ctx => ctx.raw < 0 ? 3 : 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          specialTickPlugin: true,
          legend: { display: false }
        },
        scales: {
          x: {
            position: 'bottom',
            ticks: {
              autoSkip: false,
              callback: function(value, index, ticks) {
                const label = this.getLabelForValue(value);
                const [day, month, year] = label.split('/');
                // Tableau des noms de mois en français
                const monthNames = [
                  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
                ];

                if (day === '01') {
                  const monthLabel = monthNames[parseInt(month, 10) - 1] + ' ' + year;
                  
                  const numberOfYears = Math.ceil(ticks.length / 365);

                  if(numberOfYears >= 6 && month == '01'){
                    return [day, monthLabel];
                  }
                  else if(numberOfYears < 6 && month % numberOfYears == 0){
                    return [day, monthLabel];
                  }
                }

                if(ticks.length < 50){
                  return [day];
                }
                else if(ticks.length < 100){
                  if(index % 2 === 0){
                    return [day];
                  }
                }
                else if(ticks.length < 200){
                  if(index % 4 === 0){
                    return [day];
                  }
                }

                return '';
              },
              font: { size: 11, weight: 'bold' },
              color: '#222',
              maxRotation: 0,
              minRotation: 0,
              padding: 8,
            },
            grid:{
              drawTicks: true,
              tickLength: 10,
            }
          },
          xTop: {
            position: 'top',
            ticks: {
              autoSkip: false,
              callback: function(value, index, ticks) {
                const label = this.getLabelForValue(value);
                const [day, month, year] = label.split('/');
                // Tableau des noms de mois en français
                const monthNames = [
                  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
                ];

                if(index === ticks.length - 1 || index === 0){
                  return [day + " " + monthNames[parseInt(month, 10) - 1]];
                }

                return '';
              },
              font: { size: 11, weight: 'bold' },
              color: '#222',
              maxRotation: 0,
              minRotation: 0,
              padding: 8,
            },
            grid:{
              drawTicks: true,
              tickLength: 10,
            }
          },
          y: {
            beginAtZero: true,
            suggestedMin: 0,
            ticks: { font: { weight: 'bold' } },
            grid: {
              display: true,
              lineWidth: 1,
              color: ctx => ctx.tick.value === 0 ? '#000' : '#ccc',
              borderDash: ctx => ctx.tick.value === 0 ? [0, 0] : [5, 5]
            }
          }
        }
      }
    };
  },
  methods: {
    formatDateForInput(date) {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0'); // Les mois commencent à 0
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    generateDates(startDate, endDate) {
      const dates = [];
      let current = new Date(startDate);
      const end = new Date(endDate);

      // S'assure que l'heure est à minuit pour éviter les décalages
      current.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return dates;
    },
    generateDateLabels(startDate, endDate, formatFn = d => d.toISOString().slice(0, 10)) {
      const labels = [];
      let current = new Date(startDate);
      const end = new Date(endDate);

      // S'assure que l'heure est à minuit pour éviter les décalages
      current.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      while (current <= end) {
        labels.push(formatFn(new Date(current)));
        current.setDate(current.getDate() + 1);
      }
      return labels;
    },
    updateChart() {
      if (!this.payments || !this.payments.sumAt) {
        return;
      }

      const dates = this.generateDates(this.startDate, this.endDate);
      // Pour formater ensuite :
      const labels = dates.map(d => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getFullYear()}`);

      let datas = [];
      const sumAt = this.payments.sumAt;
      const sumAtKeys = Object.keys(sumAt).sort(); // format YYYY-MM-DD, trié croissant

      for (const date of dates) {
        const key = this.formatDateForInput(date);
        if (sumAt.hasOwnProperty(key)) {
          datas.push(sumAt[key]);
        } else {
          // Cherche la dernière date <= key dans sumAtKeys
          let last = 0;
          for (let i = sumAtKeys.length - 1; i >= 0; i--) {
            if (sumAtKeys[i] <= key) {
              last = sumAt[sumAtKeys[i]];
              break;
            }
          }
          datas.push(last);
        }
      }

      // Met à jour le graphique, même si data est uniquement composée de zéros
      this.data = {
        labels: labels,
        datasets: [
          {
            label: 'Trésorerie',
            backgroundColor: '#f87979',
            data: datas,
            pointBackgroundColor: ctx => ctx.raw < 0 ? 'red' : '#f87979',
            pointRadius: ctx => ctx.raw < 0 ? 3 : 2
          }
        ]
      };

      return this.data;
    }
  },
  watch: {
    payments: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
  },
  mounted() {
    this.updateChart();
  }
}
</script>